// Shared low-poly building style — a function-coded palette plus cheap part
// makers (box, cylinder, one-part gable and pyramid roofs, a foundation slab
// and corner scaffold poles). Ported from age-of-mcp's v3/style.ts: the pure
// geometry and the colour constants are kept verbatim, with the Part type taken
// from the library.
//
// DESIGN LANGUAGE (the brief these helpers were authored for): the set targets
// a 45° orthographic camera and SPEED.
//
//  1. THE ROOF IS THE SIGN. The camera looks down, so the top face dominates
//     and nothing under a roof is ever visible. A building's identity lives
//     in its roof colour (function-coded, see ROOF_* below), its top
//     silhouette and ONE oversized iconic feature readable from above (a
//     saw blade, a loaf, an anvil, crates) — never in a wall detail.
//  2. OPEN WORK YARDS. Wherever an actor animates (work / eat / load
//     stations), the station stands on OPEN, uncovered ground so the
//     micro-animations read from above.
//  3. HARD PART BUDGET. Few big boxes and low-segment cylinders; prism/pyramid
//     roofs are ONE part (see gable / pyramid).
//  4. BOLD COLOUR BLOCKING. 3–5 flat saturated colours per building from the
//     shared palette; high roof/wall/accent contrast.
//  5. ONE DOMINANT MASS + one signature vertical (chimney, mast, derrick) so
//     the silhouette reads at any camera yaw.

import * as THREE from "three";
import type { ColorRepresentation, Part } from "../types";

// -- Function-coded palette ---------------------------------------------------
// Walls & structure.
export const PLASTER  = "#e9dcc4";   // limewashed walls
export const TIMBER   = "#5b4226";   // dark oak framing
export const WOOD     = "#9a6a38";   // mid plank wood
export const WOOD_DK  = "#73522e";   // darker plank
export const STONE    = "#8d8a82";   // dressed stone
export const STONE_DK = "#5a5a5a";   // foundation stone
export const STEEL    = "#3c4148";   // dark industrial steel
export const DOOR     = "#4a3018";

// Roofs — the function code the watcher learns once and reads everywhere.
export const ROOF_HOME   = "#c4593a";   // terracotta — homes & civic
export const ROOF_FOOD   = "#e0a33c";   // wheat — the food chain
export const ROOF_WOODIND = "#5d8c3e";  // leaf green — wood industry
export const ROOF_MINERAL = "#5e7287";  // slate — stone & mineral industry
export const ROOF_PRECIOUS = "#e3b341"; // gold — precious goods
export const ROOF_LOGI   = "#e8c832";   // hazard yellow — logistics & fuel
export const ROOF_TRADE  = "#b03a3a";   // banner red — trade & special

// Accents.
export const HAZARD  = "#2e2e30";    // pairs with ROOF_LOGI for stripes
export const CANVAS  = "#ece4d2";    // awning cloth
export const GEM     = "#46c8b2";
export const FIRE    = "#ff7a2e";    // glowing fire/oven mouths
export const CROP    = "#a8c44a";

// -- Cheap part makers --------------------------------------------------------

/** A box part in one call. */
export function box(
  w: number, h: number, d: number, color: ColorRepresentation,
  position: [number, number, number], rotation?: [number, number, number],
): Part {
  return { geometry: new THREE.BoxGeometry(w, h, d), color, position, rotation };
}

/** A low-segment cylinder part (vertical unless rotated). */
export function cyl(
  rTop: number, rBot: number, h: number, seg: number,
  color: ColorRepresentation,
  position: [number, number, number], rotation?: [number, number, number],
): Part {
  return { geometry: new THREE.CylinderGeometry(rTop, rBot, h, seg), color, position, rotation };
}

/**
 * A gable roof as ONE part: a 3-segment prism laid on its side along `axis`,
 * scaled to `span` (across the ridge) × `rise` (ridge height above the eave)
 * × `length` (along the ridge). `y` is the eave line (the roof's base plane).
 */
export function gable(
  span: number, length: number, rise: number,
  color: ColorRepresentation,
  cx: number, y: number, cz: number, axis: "x" | "z" = "z",
): Part {
  // A 3-sided cylinder is a triangular prism; the −π/2 lie-down rotation
  // seats one flat side DOWN with the apex edge UP (verified numerically —
  // +π/2 would land the prism flat-side-up). Radius 1 with cross-section
  // width 2·cos(30°)=√3 and height 1.5 (apex 1, base −0.5).
  const g = new THREE.CylinderGeometry(1, 1, 1, 3);
  const sx = span / Math.sqrt(3), sy = rise / 1.5;
  const rotation: [number, number, number] =
    axis === "z" ? [-Math.PI / 2, 0, 0] : [-Math.PI / 2, 0, Math.PI / 2];
  return {
    geometry: g, color,
    // After the lie-down rotation the prism's base plane sits 0.5·sy below
    // its origin; lift so the eave line lands on `y`.
    position: [cx, y + 0.5 * sy, cz],
    rotation,
    scale: axis === "z" ? [sx, length, sy] : [sx, length, sy],
  };
}

/** A pyramid roof as ONE part (a 4-segment cone), square `span`, `rise` tall,
 *  base plane at `y`. */
export function pyramid(
  span: number, rise: number, color: ColorRepresentation,
  cx: number, y: number, cz: number,
): Part {
  // A 4-segment cone's square base has half-diagonal = r, so r = span/√2;
  // rotate 45° so the base edges align with the axes.
  return {
    geometry: new THREE.ConeGeometry(span / Math.SQRT2, rise, 4),
    color,
    position: [cx, y + rise / 2, cz],
    rotation: [0, Math.PI / 4, 0],
  };
}

/** A rectangular foundation slab (buildings sit on a thin one). */
export function slab(
  w: number, d: number, color: ColorRepresentation = STONE_DK, t = 0.12,
): Part {
  return box(w, t, d, color, [0, t / 2, 0]);
}

/** Four corner scaffold poles for the build stages, `w`×`d` wide, `h` tall. */
export function poles(w: number, d: number, h: number): Part[] {
  const out: Part[] = [];
  for (const [x, z] of [[-w / 2, -d / 2], [w / 2, -d / 2], [-w / 2, d / 2], [w / 2, d / 2]] as const) {
    out.push(cyl(0.022, 0.022, h, 4, TIMBER, [x, h / 2, z]));
  }
  return out;
}
